import React from 'react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { CheckCircle, Clock, Users, BarChart3, Shield, Zap, Star, ArrowRight, MapPin, ShoppingBag } from 'lucide-react';

const SmallBusinessTimeClock = () => {
  const handleGetStarted = () => {
    window.location.href = '/manager-dashboard';
  };

  const handleProTrial = () => {
    window.open('https://buy.stripe.com/5kQ9ATgrw2EWb1nci42ZO0a', '_blank');
  };

  const benefits = [
    {
      title: "Save 10+ Hours Per Week",
      description: "Eliminate manual timesheet calculations and reduce payroll processing time with automated employee time tracking."
    },
    {
      title: "Improve Payroll Accuracy",
      description: "Accurate time tracking helps prevent time entry errors and unauthorized clock-ins, common issues that can affect small business payroll."
    },
    {
      title: "No Hardware Investment",
      description: "Unlike traditional time clocks, Tyme works on any device - tablets, computers, or smartphones you already own."
    },
    {
      title: "Start in 5 Minutes",
      description: "Setup is quick and simple. No complex installation or technical expertise required for your small business."
    }
  ];

  const businessTypes = [
    { type: "Retail Stores", example: "Perfect for shops, boutiques, and chain stores across the UK" },
    { type: "Restaurants & Cafes", example: "Manage staff shifts and break times efficiently" },
    { type: "Healthcare Practices", example: "Track nurse and support staff hours accurately" },
    { type: "Professional Services", example: "Monitor consultant and admin staff attendance" },
    { type: "Manufacturing", example: "Keep track of production staff and shift patterns" },
    { type: "Beauty & Wellness", example: "Manage therapist and reception staff schedules" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src="/tyme-logo-new.png" alt="Tyme Small Business Time Clock" className="h-12" />
          </a>
          <Button onClick={handleGetStarted} className="bg-gradient-to-r from-indigo-600 to-purple-600">
            Start Free Trial
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Small Business <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Time Clock Software</span> UK
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              The easiest employee time tracking solution for UK small businesses. No expensive hardware, no complex setup - just simple, affordable staff attendance management that works on any device.
            </p>
            
            {/* Key Stats */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-indigo-100">
              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600">£39/month</div>
                  <div className="text-sm text-slate-600">For up to 50 employees</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">5 minutes</div>
                  <div className="text-sm text-slate-600">Setup time</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">No hardware</div>
                  <div className="text-sm text-slate-600">Use existing devices</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">3-day Pro trial</div>
                  <div className="text-sm text-slate-600">Try Pro features free</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleProTrial}
                size="lg"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 py-4 text-lg font-semibold"
              >
                Start 3-Day Pro Trial Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-12">
            Why Small Business Owners Choose Tyme Employee Time Clock Software
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Types Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-12">
            Perfect Time Clock Solution for Every UK Small Business
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {businessTypes.map((business, index) => (
              <Card key={index} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-indigo-600" />
                    {business.type}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-sm">{business.example}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-12">
            How Tyme Small Business Time Clock Works
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-indigo-600">1</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Quick Setup</h3>
              <p className="text-slate-600 text-sm">Create your account and add your employees in minutes. No technical expertise required.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-indigo-600">2</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Staff Check-In</h3>
              <p className="text-slate-600 text-sm">Employees use their PIN to clock in/out on any device - tablet, computer, or smartphone.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-indigo-600">3</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Automated Reports</h3>
              <p className="text-slate-600 text-sm">Download accurate timesheets and attendance reports for easy payroll processing.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-slate-100 to-slate-200">
        <div className="container mx-auto text-center">
          <div className="max-w-3xl mx-auto">
            <div className="flex justify-center mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
              ))}
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Designed for UK Small Business Success
            </h3>
            <p className="text-lg text-slate-600 mb-8">
              Our time tracking solution is built specifically for the unique needs of UK small businesses. 
              Simple setup, affordable pricing, and features that actually help you save time and reduce administrative burden.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-3" />
                <h4 className="font-semibold text-slate-800 mb-2">Quick Setup</h4>
                <p className="text-sm text-slate-600">Get your team tracking time in under 5 minutes</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-3" />
                <h4 className="font-semibold text-slate-800 mb-2">No Hardware Needed</h4>
                <p className="text-sm text-slate-600">Works on any device with an internet connection</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-3" />
                <h4 className="font-semibold text-slate-800 mb-2">UK Support</h4>
                <p className="text-sm text-slate-600">Local support team available during UK business hours</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Solutions Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
            Related Solutions for Your Business
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-indigo-600" />
                  Retail-Specific Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">
                  If you're running a retail business, discover specialized attendance features for shops, boutiques, and chain stores.
                </p>
                <a 
                  href="/retail-attendance-software" 
                  className="text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-2 group"
                >
                  Explore Retail Solutions
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  Compare with Competitors
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">
                  See how Tyme compares to Kronos, Mitrefinch, and BambooHR for UK businesses seeking better alternatives.
                </p>
                <a 
                  href="/time-tracking-software" 
                  className="text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-2 group"
                >
                  View Detailed Comparison
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <p className="text-slate-600 mb-4">
              Want to see all our employee attendance features?
            </p>
            <a 
              href="/" 
              className="text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-2"
            >
              ← Back to Main Features Overview
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Simplify Your Small Business Time Tracking?
          </h2>
          <p className="text-xl mb-8 text-indigo-100 max-w-2xl mx-auto">
            Join UK small businesses using Tyme to save time, streamline processes, and simplify payroll management. 
            Start your 3-day Pro trial today - experience all Pro features free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleProTrial}
              size="lg"
              className="bg-white text-indigo-600 hover:bg-slate-50 px-8 py-4 text-lg font-semibold"
            >
              Start 3-Day Pro Trial Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
          
          <div className="mt-8 text-indigo-200 text-sm">
            ✓ 3-day Pro trial ✓ No setup fees ✓ Cancel anytime ✓ UK-based support
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="container mx-auto text-center">
          <img src="/tyme-logo-new.png" alt="Tyme" className="h-12 mx-auto mb-6" />
          <p className="text-slate-400 mb-4">Small business time clock software for UK businesses</p>
          <p className="text-slate-500 text-sm">
            Employee time tracking • Staff attendance management • Payroll automation
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SmallBusinessTimeClock;